import React, { useEffect, useRef, useState } from "react";
import "../../assets/css/galleryCard.scss";
import "../../config";
import { useTranslation } from "../../contexts/TranslationContext";
import Utilities from "../../assets/js/Utilities";

const HistoryCard = ({ record, index, probeImage, accountAnalyze, onViewResults, onZoomClick }) => {
  const { language } = useTranslation();
  const imgRef = useRef(null);
  const [imgIsLoaded, setImgIsLoaded] = useState(false);

  // Get image URL from probe image data
  const getImageUrl = () => {
    if (probeImage?.PublicPath) {
      return probeImage.PublicPath;
    }
    if (probeImage?.ImagePath && typeof probeImage.ImagePath === 'string') {
      return probeImage.ImagePath;
    }
    if (probeImage?.RelativePath) {
      const baseUrl = global.config.images3?.link;
      if (!baseUrl) return "";
      const relativePath = probeImage.RelativePath.startsWith("/")
        ? probeImage.RelativePath.substring(1)
        : probeImage.RelativePath;
      return baseUrl.endsWith("/") ? `${baseUrl}${relativePath}` : `${baseUrl}/${relativePath}`;
    }
    return "";
  };

  const imageUrl = getImageUrl();

  // Lazy load image via IntersectionObserver (same pattern as GalleryCard)
  useEffect(() => {
    const imgElement = imgRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const lazyImage = entry.target;
          lazyImage.src = lazyImage.dataset.src;
          setImgIsLoaded(true);
          observer.unobserve(lazyImage);
        }
      },
      { threshold: 0.1 }
    );

    if (imgElement) {
      observer.observe(imgElement);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Format date part (MM/dd/yyyy) from .NET ticks
  const getDatePart = (ticks) => {
    if (!ticks) return language.common?.na || "N/A";
    try {
      const localDate = Utilities.ConvertTicksToDateTime(ticks);
      const dd = String(localDate.getDate()).padStart(2, "0");
      const mm = String(localDate.getMonth() + 1).padStart(2, "0");
      const yyyy = localDate.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    } catch {
      return language.common?.na || "N/A";
    }
  };

  // Format time part (HH:mm:ss) from .NET ticks
  const getTimePart = (ticks) => {
    if (!ticks) return language.common?.na || "N/A";
    try {
      const localDate = Utilities.ConvertTicksToDateTime(ticks);
      const hh = String(localDate.getHours()).padStart(2, "0");
      const min = String(localDate.getMinutes()).padStart(2, "0");
      const sec = String(localDate.getSeconds()).padStart(2, "0");
      return `${hh}:${min}:${sec}`;
    } catch {
      return language.common?.na || "N/A";
    }
  };

  // No image placeholder
  if (!imageUrl) {
    return (
      <div className="galleryCard">
        <div className={
          "gallery-index" +
          (index >= 10000 ? " over-10k"
            : index >= 1000 ? " over-1000"
              : index >= 100 ? " over-100"
                : "")
        }>
          {index?.toLocaleString() || 0}
        </div>
        <div className="preview-img">
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-secondary)"
          }}>
            <i className="fa fa-image" style={{ fontSize: "2rem", marginBottom: "0.5rem", opacity: 0.5 }}></i>
            <span style={{ fontSize: "0.8rem", fontWeight: 500 }}>{language.gallery?.noImagesAvailable || "No Image"}</span>
          </div>
        </div>
        <div className="gallery-data">
          <div className="row-data">ID: {record?.ID || record?.Id || language.common?.na || "N/A"}</div>
          <div className="row-data">
            <div className="row-data-value">
              <i className="fa fa-calendar"></i>
              <span>{getDatePart(record?.CreatedTicks)}</span>
            </div>
          </div>
          <div className="row-data">
            <div className="row-data-value">
              <i className="fa fa-clock-o"></i>
              <span>{getTimePart(record?.CreatedTicks)}</span>
            </div>
          </div>
          <div className="row-data">
            <div className="row-data-value">
              <i className="fa fa-user"></i>
              <span>{accountAnalyze?.Name || language.common?.na || 'N/A'}</span>
            </div>
          </div>
        </div>
        <div className="gallery-actions">
          <button className="btn-action-full" onClick={(e) => {
            e.stopPropagation();
            if (onViewResults) onViewResults(record?.ID || record?.Id);
          }}>
            <i className="fa fa-eye"></i>
            <span>{language.history?.viewResults || "View Results"}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="galleryCard" onDoubleClick={() => {
      if (onViewResults) onViewResults(record?.ID || record?.Id);
    }}>
      <div className={
        "gallery-index" +
        (index >= 10000 ? " over-10k"
          : index >= 1000 ? " over-1000"
            : index >= 100 ? " over-100"
              : "")
      }>
        {index?.toLocaleString() || 0}
      </div>
      <div className="preview-img">
        <img
          ref={imgRef}
          className={`lazyImg${imgIsLoaded ? "" : " loading"}`}
          data-src={imageUrl}
          alt="Probe"
        />
        {onZoomClick && (
          <div className="zoom-icon" onClick={(e) => {
            e.stopPropagation();
            onZoomClick(probeImage?.Image_ID || probeImage?.ID);
          }}>
            <i className="fa fa-search-plus"></i>
          </div>
        )}
      </div>
      <div className="gallery-data">
        <div className="row-data">ID: {record?.ID || record?.Id || language.common?.na || "N/A"}</div>
        <div className="row-data">
          <div className="row-data-value">
            <i className="fa fa-calendar"></i>
            <span>{getDatePart(record?.CreatedTicks)}</span>
          </div>
        </div>
        <div className="row-data">
          <div className="row-data-value">
            <i className="fa fa-clock-o"></i>
            <span>{getTimePart(record?.CreatedTicks)}</span>
          </div>
        </div>
        <div className="row-data">
          <div className="row-data-value">
            <i className="fa fa-user"></i>
            <span>{accountAnalyze?.Name || language.common?.na || 'N/A'}</span>
          </div>
        </div>
      </div>
      <div className="gallery-actions">
        <button className="btn-action-full" onClick={(e) => {
          e.stopPropagation();
          if (onViewResults) onViewResults(record?.ID || record?.Id);
        }}>
          <i className="fa fa-eye"></i>
          <span>{language.history?.viewResults || "View Results"}</span>
        </button>
      </div>
    </div>
  );
};

export default HistoryCard;
